import type {
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core"

import {
  DragOverlay,
  DndContext,
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable"

import type { FC } from "react"

import {
  useCallback,
  useState,
  useMemo,
} from "react"

import { SortableListContext } from "./SortableListContext"

type Item = {
  id: string;
}

export interface Props<
  T extends Item | string,
  Component = T extends string ? FC<Item> : FC<Exclude<T, string>>,
> {
  items: Array<T>;
  onChange: (items: Array<T>) => void;
  SortableItem: Component;
  OverlayItem: Component;
}

export function SortableList<T extends Item | string = string>(props: Props<T>): JSX.Element {
  const {
    OverlayItem,
    SortableItem,
    items,
    onChange,
  } = props

  const [
    activeItem,
    setActiveItem,
  ] = useState<T | null>(null)

  const mouseSensor = useSensor(
    MouseSensor,
    {
      activationConstraint: {
        distance: 10,
      },
    },
  )

  const touchSensor = useSensor(
    TouchSensor,
    {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    },
  )

  const keyboardSensor = useSensor(
    KeyboardSensor,
    { coordinateGetter: sortableKeyboardCoordinates },
  )

  const sensors = useSensors(
    mouseSensor,
    touchSensor,
    keyboardSensor,
  )

  const handleDragCancel = useCallback(
    () => {
      setActiveItem(null)
    },
    [setActiveItem],
  )

  const handleDragStart = useCallback(
    (e: DragStartEvent) => {
      const newActiveItem = items.find(
        (item) => typeof item === "string"
          ? item === e.active.id
          : item.id === e.active.id,
      )

      if (newActiveItem) {
        setActiveItem(newActiveItem)
      }
    },
    [
      setActiveItem,
      items,
    ],
  )

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const {
        active,
        over,
      } = e

      if (!over) {
        return
      }

      if (active.id !== over.id) {
        const oldIndex = items.findIndex(
          (item) => typeof item === "string"
            ? item === active.id
            : item.id === active.id,
        )

        const newIndex = items.findIndex(
          (item) => typeof item === "string"
            ? item === over.id
            : item.id === over.id,
        )

        onChange(
          arrayMove(
            items,
            oldIndex,
            newIndex,
          ),
        )
      }

      setActiveItem(null)
    },
    [
      items,
      onChange,
    ],
  )

  const contextValue = useMemo(
    () => {
      if (activeItem === null) {
        return {
          activeId: null,
        }
      }

      const activeId = typeof activeItem === "string"
        ? activeItem
        : activeItem.id

      return {
        activeId,
      }
    },
    [activeItem],
  )

  const StringOverlay = OverlayItem as FC<Item>
  const ObjectOverlay = OverlayItem as FC<T>
  const StringSortable = SortableItem as FC<Item>
  const ObjectSortable = SortableItem as FC<T>

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableListContext.Provider value={contextValue}>
        <SortableContext
          items={items}
          strategy={rectSortingStrategy}
        >
          {items.map((item: T) => {
            if (typeof item === "string") {
              return (
                <StringSortable
                  key={item}
                  id={item}
                />
              )
            }

            return (
              <ObjectSortable
                key={item.id}
                isActive={item === activeItem}
                {...item as Exclude<T, string>}
              />
            )
          })}
        </SortableContext>
        <DragOverlay>
          {activeItem !== null && (
            typeof activeItem === "string"
              ? <StringOverlay id={activeItem} />
              : <ObjectOverlay {...activeItem as Exclude<T, string>} />
          )}
        </DragOverlay>
      </SortableListContext.Provider>
    </DndContext>
  )
}
